import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AiUsagePanel } from "@/components/AiUsagePanel";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { withBudget, BudgetConfirmHost, CANCELLED } from "@/components/BudgetConfirm";

const showErr = (err) => { if (err?.message !== CANCELLED) toast.error(apiError(err)); };
import { Download, ImageDown, Loader2, Sparkles, Trash2, RotateCw, ExternalLink, UploadCloud, Images, Maximize2 } from "lucide-react";
import { toast } from "sonner";

const B = process.env.REACT_APP_BACKEND_URL;
const thumbUrl = (id) => `${B}/api/photos/${id}/thumb`;
const editUrl = (id, size = "thumb") => `${B}/api/photo-edits/${id}/image?size=${size}`;

function PhotoTile({ p, checked, onToggle }) {
  return (
    <button onClick={() => onToggle(p.id)} data-testid={`photo-tile-${p.id}`}
      className={`group relative aspect-[4/3] overflow-hidden rounded-lg border-2 transition-colors ${checked ? "border-primary" : "border-transparent hover:border-border"}`}>
      <img src={thumbUrl(p.id)} alt={p.caption || "property"} loading="lazy" className="h-full w-full object-cover" />
      <span className="absolute left-2 top-2 rounded bg-background/80 p-0.5"><Checkbox checked={checked} className="pointer-events-none" /></span>
    </button>
  );
}

function EditCard({ e, checked, onToggle, onChange }) {
  const [after, setAfter] = useState(true);
  const [confirmDel, setConfirmDel] = useState(false);
  const act = (fn) => fn().then(onChange).catch(showErr);
  const done = e.status === "done" || e.status === "upscaling";
  const busy = e.status !== "done";
  return (
    <div className={`overflow-hidden rounded-lg border bg-card ${checked ? "border-primary" : "border-border"}`} data-testid={`photo-edit-${e.id}`}>
      <div className="relative aspect-[4/3] bg-secondary" onMouseDown={() => setAfter(false)} onMouseUp={() => setAfter(true)} onMouseLeave={() => setAfter(true)}
        onTouchStart={() => setAfter(false)} onTouchEnd={() => setAfter(true)}>
        <img src={done && after ? editUrl(e.id) : thumbUrl(e.photo_id)} alt="" className="h-full w-full select-none object-cover" draggable={false} />
        {busy && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-background/60 text-xs">
            {e.status === "error" ? <span className="px-3 text-center text-rose-400">{e.error || "Failed"}</span>
              : <><Loader2 className="h-5 w-5 animate-spin text-primary" />{{ queued: "Queued", upscaling: "Upscaling…" }[e.status] || "Editing…"}</>}
          </div>
        )}
        {e.upscaled && <span className="absolute right-2 top-2 rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground" data-testid={`photo-edit-hd-${e.id}`}>HD {e.width}px</span>}
        {done && <span className="absolute bottom-2 right-2 rounded bg-background/80 px-1.5 py-0.5 text-[10px]">{after ? "After · hold for before" : "Before"}</span>}
        {done && <button onClick={() => onToggle(e.id)} className="absolute left-2 top-2 rounded bg-background/80 p-0.5" data-testid={`photo-edit-select-${e.id}`}><Checkbox checked={checked} className="pointer-events-none" /></button>}
      </div>
      <div className="flex items-center gap-1 px-2.5 py-2">
        <span className="mr-auto truncate text-xs font-medium" title={e.upscale_error || e.note}>{e.pro ? "Pro · " : ""}{e.preset_label}{e.note ? ` · ${e.note}` : ""}{e.upscale_error ? " · upscale failed" : ""}</span>
        {e.status === "done" && !e.upscaled && <button onClick={() => act(() => withBudget((c) => api.post(`/photo-edits/${e.id}/upscale`, null, { params: { confirm: c } })))} className="rounded p-1 text-muted-foreground hover:text-foreground" title="Upscale to original size" data-testid={`photo-edit-upscale-${e.id}`}><Maximize2 className="h-3.5 w-3.5" /></button>}
        {done && <a href={editUrl(e.id, "full")} target="_blank" rel="noreferrer" className="rounded p-1 text-muted-foreground hover:text-foreground" title="Open full size"><ExternalLink className="h-3.5 w-3.5" /></a>}
        {done && <a href={editUrl(e.id, "download")} className="rounded p-1 text-muted-foreground hover:text-foreground" title="Download" data-testid={`photo-edit-download-${e.id}`}><Download className="h-3.5 w-3.5" /></a>}
        {e.status === "error" && <button onClick={() => act(() => withBudget((c) => api.post(`/photo-edits/${e.id}/retry`, null, { params: { confirm: c } })))} className="rounded p-1 text-muted-foreground hover:text-foreground" title="Retry" data-testid={`photo-edit-retry-${e.id}`}><RotateCw className="h-3.5 w-3.5" /></button>}
        <button onClick={() => setConfirmDel(true)} className="rounded p-1 text-muted-foreground hover:text-rose-400" title="Delete" data-testid={`photo-edit-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent data-testid={`photo-edit-delete-dialog-${e.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this AI edit?</AlertDialogTitle>
            <AlertDialogDescription>The {e.preset_label} edit will be removed from this property. The original Aryeo photo isn't affected. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`photo-edit-delete-cancel-${e.id}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => act(() => api.delete(`/photo-edits/${e.id}`))} className="bg-rose-600 hover:bg-rose-700" data-testid={`photo-edit-delete-confirm-${e.id}`}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AdjustBar({ presets, count, onApply, busy }) {
  const [preset, setPreset] = useState("green_grass");
  const [note, setNote] = useState("");
  const [upscale, setUpscale] = useState(false);
  const [pro, setPro] = useState(false);
  return (
    <div className="sticky top-0 z-10 rounded-xl border border-primary/30 bg-card/95 p-4 shadow-lg backdrop-blur" data-testid="photo-adjust-bar">
      <div className="mb-2 text-xs text-muted-foreground"><span className="font-semibold text-foreground">{count} selected</span> · choose one AI adjustment for all of them. Only that change is made; the rest of the photo stays the same.</div>
      <div className="flex flex-wrap gap-1.5">
        {presets.map((p) => (
          <button key={p.key} onClick={() => setPreset(p.key)} data-testid={`photo-preset-${p.key}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${preset === p.key ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>{p.label}</button>
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="photo-adjust-note"
          placeholder={preset === "custom" ? "Describe the change, e.g. remove the car from the driveway" : "Optional extra detail, e.g. keep the flower beds"} />
        <Button onClick={() => onApply(preset, note, upscale, pro)} disabled={busy || (preset === "custom" && !note.trim())} className="gap-2 whitespace-nowrap" data-testid="photo-apply-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Apply to {count} photo{count === 1 ? "" : "s"}
        </Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={pro} onCheckedChange={setPro} data-testid="photo-pro-toggle" />
          Pro quality (Nano Banana Pro: more realistic, about 2x the cost)
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={upscale} onCheckedChange={setUpscale} data-testid="photo-upscale-toggle" />
          Upscale to the original photo size (AI upscaler, small extra cost)
        </label>
      </div>
    </div>
  );
}

export const PropertyPhotos = ({ order }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [selEdits, setSelEdits] = useState([]);
  const [importing, setImporting] = useState(false);
  const [applying, setApplying] = useState(false);
  const load = useCallback(() => api.get(`/orders/${order.id}/photos`).then(({ data: d }) => {
    setData(d);
    const done = new Set(d.edits.filter((e) => e.status === "done").map((e) => e.id));
    setSelEdits((s) => s.filter((id) => done.has(id)));
  }).catch((e) => toast.error(apiError(e))), [order.id]);
  useEffect(() => { load(); }, [load]);
  const pending = (data?.edits || []).some((e) => ["queued", "running", "upscaling"].includes(e.status));
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [pending, load]);

  const toggle = (setter) => (id) => setter((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const doImport = async () => {
    setImporting(true);
    try {
      const { data: r } = await api.post(`/orders/${order.id}/photos/import`);
      toast.success(r.found ? `Found ${r.found} photos in Aryeo · ${r.added} new` : "No photos loaded in Aryeo yet");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setImporting(false); }
  };
  const apply = async (preset, note, upscale, pro) => {
    setApplying(true);
    try {
      let queued = 0;
      let confirmed = false;
      for (let i = 0; i < sel.length; i += 50) {
        const chunk = sel.slice(i, i + 50);
        const { data: r } = await withBudget((c) => {
          confirmed = confirmed || c;
          return api.post(`/orders/${order.id}/photos/edits`, { photo_ids: chunk, preset, note, upscale, pro, confirm_over_budget: confirmed });
        });
        queued += r.queued;
      }
      toast.success(`${queued} AI edit${queued === 1 ? "" : "s"} started`);
      setSel([]);
      load();
    } catch (e) { showErr(e); } finally { setApplying(false); }
  };
  const upscaleSel = async () => {
    const ids = selEdits.filter((id) => data.edits.some((e) => e.id === id && e.status === "done" && !e.upscaled));
    if (!ids.length) return toast.info("Those edits are already upscaled");
    let confirmed = false;
    const res = [];
    for (const id of ids) {
      try {
        await withBudget((c) => { confirmed = confirmed || c; return api.post(`/photo-edits/${id}/upscale`, null, { params: { confirm: confirmed } }); });
        res.push(true);
      } catch (err) {
        showErr(err);
        if (err?.message === CANCELLED) break;
        res.push(false);
      }
    }
    const ok = res.filter(Boolean).length;
    if (ok) toast.success(`Upscaling ${ok} photo${ok === 1 ? "" : "s"}`);
    load();
  };
  const zipUrl = `${B}/api/orders/${order.id}/photos/edits/zip?ids=${selEdits.join(",")}`;
  const sendToAryeo = () => {
    window.location.href = zipUrl;
    if (order.listing_url) window.open(order.listing_url, "_blank", "noreferrer");
    toast.info("Downloading the edits. Drag them into the listing's photos in the Aryeo tab that just opened.", { duration: 8000 });
  };

  if (!data) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  const photos = data.photos, edits = data.edits.filter((e) => photos.some((p) => p.id === e.photo_id));
  const doneEdits = edits.filter((e) => e.status === "done");
  const allEditsSel = doneEdits.length > 0 && doneEdits.every((e) => selEdits.includes(e.id));
  return (
    <div className="space-y-6" data-testid="property-photos">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={doImport} disabled={importing} variant="outline" className="gap-2" data-testid="photos-import-btn">
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageDown className="h-4 w-4" />} {photos.length ? "Re-import from Aryeo" : "Import from Aryeo"}
        </Button>
        {photos.length > 0 && <>
          <span className="text-xs text-muted-foreground" data-testid="photos-count">{photos.length} photos</span>
          <button onClick={() => setSel(sel.length === photos.length ? [] : photos.map((p) => p.id))} className="ml-auto text-xs text-primary hover:underline" data-testid="photos-select-all">
            {sel.length === photos.length ? "Clear selection" : "Select all"}
          </button>
        </>}
      </div>
      {sel.length > 0 && <AdjustBar presets={data.presets} count={sel.length} onApply={apply} busy={applying} />}
      {photos.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="photos-empty">
          <Images className="mx-auto mb-2 h-6 w-6 text-primary" />Click “Import from Aryeo” to pull in small previews of this property's photos.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="photos-grid">
          {photos.map((p) => <PhotoTile key={p.id} p={p} checked={sel.includes(p.id)} onToggle={toggle(setSel)} />)}
        </div>
      )}
      {edits.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="photo-edits-panel">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /><span className="text-sm font-semibold">AI edits</span>
            <span className="text-xs text-muted-foreground">({edits.length})</span>
            {doneEdits.length > 0 && (
              <button onClick={() => setSelEdits(allEditsSel ? [] : doneEdits.map((e) => e.id))} className="text-xs text-primary hover:underline" data-testid="photo-edits-select-all">
                {allEditsSel ? "Clear selection" : `Select all (${doneEdits.length})`}
              </button>
            )}
            <div className="ml-auto flex flex-wrap gap-2">
              <Button size="sm" variant="outline" disabled={!selEdits.length} onClick={upscaleSel} className="gap-1.5" data-testid="photo-edits-upscale-btn">
                <Maximize2 className="h-3.5 w-3.5" /> Upscale{selEdits.length ? ` ${selEdits.length}` : ""}
              </Button>
              <Button size="sm" variant="outline" disabled={!selEdits.length} asChild={!!selEdits.length} className="gap-1.5" data-testid="photo-edits-zip-btn">
                {selEdits.length ? <a href={zipUrl}><Download className="h-3.5 w-3.5" /> Download {selEdits.length}</a> : <span className="inline-flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> Download</span>}
              </Button>
              <Button size="sm" disabled={!selEdits.length} onClick={sendToAryeo} className="gap-1.5" data-testid="photo-edits-aryeo-btn">
                <UploadCloud className="h-3.5 w-3.5" /> Send to Aryeo
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {edits.map((e) => <EditCard key={e.id} e={e} checked={selEdits.includes(e.id)} onToggle={toggle(setSelEdits)} onChange={load} />)}
          </div>
        </div>
      )}
      <BudgetConfirmHost />
      <AiUsagePanel orderId={order.id} refreshKey={(data.edits || []).filter((e) => e.status === "done").length + (data.edits || []).filter((e) => e.upscaled).length} />
    </div>
  );
};
